import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type {
  BriefDto,
  BriefArchiveRowDto,
  FeedRowDto,
  EntityDto,
  NewsItemDto,
  DigitalSummaryDto,
  DigitalItemsDto,
  StocksResponseDto,
  UsageDto,
  DebugSnapshotDto,
  QuartzJobDto,
  DebugErrorsDto,
} from '@/types/dto';
import type { BriefWindow } from '@/components/common/WindowTabs';

export function useBriefLatest(window: BriefWindow) {
  return useQuery({
    queryKey: ['brief', 'latest', window],
    queryFn: () => api.get<BriefDto>(`/api/hmhwatch/brief/latest?window=${window}`),
    retry: (failureCount, err: unknown) => {
      const status = (err as { status?: number })?.status;
      if (status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useBriefByDate(date: string, window: BriefWindow) {
  return useQuery({
    queryKey: ['brief', date, window],
    queryFn: () => api.get<BriefDto>(`/api/hmhwatch/brief/${date}?window=${window}`),
    enabled: !!date,
  });
}

export function useBriefsArchive(limit = 100) {
  return useQuery({
    queryKey: ['briefs', limit],
    queryFn: () => api.get<BriefArchiveRowDto[]>(`/api/hmhwatch/briefs?limit=${limit}`),
  });
}

export function useFeed(params: { source: string; minScore: number; days: number }) {
  const q = new URLSearchParams();
  if (params.source) q.set('source', params.source);
  if (params.minScore > 0) q.set('minScore', String(params.minScore));
  q.set('days', String(params.days));
  return useQuery({
    queryKey: ['feed', params],
    queryFn: () => api.get<FeedRowDto[]>(`/api/hmhwatch/feed?${q.toString()}`),
  });
}

export function useFeedSourceTypes() {
  return useQuery({
    queryKey: ['feed', 'source-types'],
    queryFn: () => api.get<string[]>(`/api/hmhwatch/feed/source-types`),
    staleTime: 5 * 60_000,
  });
}

export function useEntities() {
  return useQuery({
    queryKey: ['entities'],
    queryFn: () => api.get<EntityDto[]>(`/api/hmhwatch/entities`),
    staleTime: 5 * 60_000,
  });
}

export function useEntity(id: string | undefined) {
  return useQuery({
    queryKey: ['entity', id],
    queryFn: () => api.get<EntityDto>(`/api/hmhwatch/entities/${id}`),
    enabled: !!id,
  });
}

export function useEntityNews(id: string | undefined, limit = 100) {
  return useQuery({
    queryKey: ['entity', id, 'news', limit],
    queryFn: () => api.get<NewsItemDto[]>(`/api/hmhwatch/entities/${id}/news?limit=${limit}`),
    enabled: !!id,
  });
}

export function useDigitalSummary(window: BriefWindow) {
  return useQuery({
    queryKey: ['digital', 'summary', window],
    queryFn: () => api.get<DigitalSummaryDto>(`/api/hmhwatch/digital/summary?window=${window}`),
  });
}

export function useDigitalItems(params: { days: number; minScore: number; source: string }) {
  const q = new URLSearchParams();
  q.set('days', String(params.days));
  q.set('minScore', String(params.minScore));
  q.set('source', params.source);
  return useQuery({
    queryKey: ['digital', 'items', params],
    queryFn: () => api.get<DigitalItemsDto>(`/api/hmhwatch/digital/items?${q.toString()}`),
  });
}

export function useStocks(window: BriefWindow) {
  return useQuery({
    queryKey: ['stocks', window],
    queryFn: () => api.get<StocksResponseDto>(`/api/hmhwatch/stocks?window=${window}`),
  });
}

export function useUsage(month: string) {
  return useQuery({
    queryKey: ['usage', month],
    queryFn: () => api.get<UsageDto>(`/api/hmhwatch/usage?month=${month}`),
  });
}

const DEBUG_REFETCH_MS = 8_000;

export function useDebugSnapshot(enabled: boolean) {
  return useQuery({
    queryKey: ['debug', 'snapshot'],
    queryFn: () => api.get<DebugSnapshotDto>('/api/hmhwatch/debug/snapshot'),
    enabled,
    refetchInterval: enabled ? DEBUG_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  });
}

export function useQuartzJobs(enabled: boolean) {
  return useQuery({
    queryKey: ['debug', 'quartz'],
    queryFn: () => api.get<QuartzJobDto[]>('/api/hmhwatch/debug/quartz'),
    enabled,
    refetchInterval: enabled ? DEBUG_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  });
}

export function useDebugErrors(enabled: boolean) {
  return useQuery({
    queryKey: ['debug', 'errors'],
    queryFn: () => api.get<DebugErrorsDto>('/api/hmhwatch/debug/errors'),
    enabled,
    refetchInterval: enabled ? DEBUG_REFETCH_MS : false,
    refetchIntervalInBackground: false,
  });
}
